"use client";

import Image from "next/image";
import { useState, useRef, useEffect, type ChangeEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  X,
  Heart,
  MessageSquare,
  Mic,
  Video,
  Send,
  Star,
  Play,
  Square,
  RotateCcw,
  Pause,
  Image as ImageIcon,
} from "lucide-react";
import {
  submitEventReview,
  type ReviewMediaKind,
} from "@/lib/services/reviewService";
import {
  convertAudioToMp3,
  convertImageToJpeg,
  convertVideoToMp4,
} from "@/lib/media-conversion";

interface GratitudeComposerProps {
  eventId: string;
  sessionId?: string;
  organizationId?: string;
  onClose: () => void;
  onSent?: (gratitudeData: {
    type: MessageType;
    content: string;
    rating: number;
  }) => void; // Added optional callback for when message is sent
}

type MessageType = "text" | "voice" | "video" | "image";
type RecordingState = "idle" | "recording" | "recorded";

export function GratitudeComposer({
  eventId,
  sessionId,
  organizationId,
  onClose,
  onSent,
}: GratitudeComposerProps) {
  const [messageType, setMessageType] = useState<MessageType>("text");
  const [textMessage, setTextMessage] = useState("");
  const [rating, setRating] = useState(5);
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStage, setSubmitStage] = useState<
    "idle" | "converting" | "uploading"
  >("idle");
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const recordingPreviewRef = useRef<HTMLVideoElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    if (messageType !== "video") {
      return;
    }

    const previewElement = recordingPreviewRef.current;

    if (!previewElement) {
      return;
    }

    if (recordingState === "recording" && streamRef.current) {
      previewElement.srcObject = streamRef.current;
      const playPromise = previewElement.play();

      if (playPromise) {
        playPromise.catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError") {
            return;
          }
          console.error("Failed to play recording preview", error);
        });
      }
    } else {
      previewElement.pause();
      previewElement.srcObject = null;
    }
  }, [messageType, recordingState]);

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const trimmedText = textMessage.trim();
      const text = trimmedText.length > 0 ? trimmedText : undefined;

      if (!sessionId || !organizationId) {
        throw new Error("לא נמצאו פרטי האירוע הדרושים לשליחת הביקורת");
      }

      let processedMedia: { blob: Blob; kind: ReviewMediaKind } | undefined;

      if (messageType !== "text" && mediaBlob) {
        setSubmitStage("converting");
        try {
          const normalizedBlob = await normalizeMediaBlob(
            mediaBlob,
            messageType
          );
          processedMedia = {
            blob: normalizedBlob,
            kind:
              messageType === "voice"
                ? "audio"
                : (messageType as ReviewMediaKind),
          };
        } catch (error) {
          console.error("Failed to normalize media", error);
          const message =
            error instanceof Error
              ? error.message
              : "לא ניתן היה להמיר את הקובץ לפורמט נתמך";
          throw new Error(message);
        }
      }

      setSubmitStage("uploading");

      await submitEventReview({
        eventId,
        rating,
        text,
        sessionId,
        organizationId,
        media: processedMedia,
      });

      const gratitudeData = {
        type: messageType,
        content: textMessage,
        rating,
      } as { type: MessageType; content: string; rating: number };

      onSent?.(gratitudeData);
      onClose();
    } catch (error) {
      console.error("Failed to submit gratitude message", error);
      const message =
        error instanceof Error
          ? error.message
          : "שגיאה בשליחת ההודעה. אנא נסה שוב.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
      setSubmitStage("idle");
    }
  };

  const startRecording = async () => {
    try {
      // Reset chunks
      chunksRef.current = [];

      // Get user media
      const constraints =
        messageType === "video"
          ? { video: true, audio: true }
          : { audio: true };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: messageType === "video" ? "video/webm" : "audio/webm",
        });
        setMediaBlob(blob);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());

        streamRef.current = null;
        mediaRecorderRef.current = null;
        chunksRef.current = [];
      };

      // Start recording
      mediaRecorder.start();
      setRecordingState("recording");
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 59) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error accessing media devices:", error);
      alert("לא ניתן לגשת למצלמה/מיקרופון. אנא בדוק את הרשאות הדפדפן.");
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      setRecordingState("recorded");
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
  };

  const clearMedia = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setRecordingState("idle");
    setRecordingTime(0);
    setMediaBlob(null);
    setIsPlaying(false);
    setImagePreview(null);
  };

  const resetRecording = () => {
    setSubmitError(null);
    clearMedia();
  };

  const playRecording = () => {
    if (!mediaBlob) return;

    const url = URL.createObjectURL(mediaBlob);

    if (messageType === "video" && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = url;
      const playPromise = videoRef.current.play();
      if (playPromise) {
        playPromise.catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError") {
            return;
          }
          console.error("Failed to play recorded video", error);
        });
      }
      setIsPlaying(true);

      videoRef.current.onended = () => {
        setIsPlaying(false);
      };
    } else if (messageType === "voice" && audioRef.current) {
      audioRef.current.src = url;
      const playPromise = audioRef.current.play();
      if (playPromise) {
        playPromise.catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError") {
            return;
          }
          console.error("Failed to play recorded audio", error);
        });
      }
      setIsPlaying(true);

      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    }
  };

  const pauseRecording = () => {
    if (messageType === "video" && videoRef.current) {
      videoRef.current.pause();
    } else if (messageType === "voice" && audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
  };

  const handleMessageTypeChange = (nextType: MessageType) => {
    if (nextType === messageType) {
      return;
    }

    const hasExistingMedia =
      mediaBlob !== null || recordingState === "recording";

    if (hasExistingMedia) {
      const shouldReset = window.confirm(
        "שינוי סוג ההודעה ימחק את המדיה שהועלתה. האם להמשיך?"
      );

      if (!shouldReset) {
        return;
      }

      clearMedia();
    } else if (messageType !== "text") {
      clearMedia();
    }

    setSubmitError(null);
    setMessageType(nextType);
  };

  const handleImageFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setSubmitError("ניתן להעלות קבצי תמונה בלבד");
      event.target.value = "";
      return;
    }

    setSubmitError(null);
    setMediaBlob(file);
    setImagePreview(URL.createObjectURL(file));
    event.target.value = "";
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-serif font-bold">
              הודעת תודה
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-red-500" />
            <span className="text-sm text-muted-foreground">
              תודה למתנדב/ת על העזרה
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageFileChange}
          />
          {/* Star Rating */}
          <div className="space-y-2">
            <label className="text-sm font-medium">דרג את השירות</label>
            <div className="flex items-center justify-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= rating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-xs text-center text-muted-foreground">
              {rating === 5
                ? "מעולה!"
                : rating === 4
                ? "טוב מאוד"
                : rating === 3
                ? "טוב"
                : rating === 2
                ? "בסדר"
                : "נדרש שיפור"}
            </p>
          </div>

          {/* Message Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">סוג ההודעה</label>
            <div className="flex gap-2">
              <Button
                variant={messageType === "text" ? "default" : "outline"}
                size="sm"
                onClick={() => handleMessageTypeChange("text")}
                className="flex-1 gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                טקסט
              </Button>
              <Button
                variant={messageType === "voice" ? "default" : "outline"}
                size="sm"
                onClick={() => handleMessageTypeChange("voice")}
                className="flex-1 gap-2"
              >
                <Mic className="h-4 w-4" />
                קול
              </Button>
              <Button
                variant={messageType === "video" ? "default" : "outline"}
                size="sm"
                onClick={() => handleMessageTypeChange("video")}
                className="flex-1 gap-2"
              >
                <Video className="h-4 w-4" />
                וידאו
              </Button>
              <Button
                variant={messageType === "image" ? "default" : "outline"}
                size="sm"
                onClick={() => handleMessageTypeChange("image")}
                className="flex-1 gap-2"
              >
                <ImageIcon className="h-4 w-4" />
                תמונה
              </Button>
            </div>
          </div>

          {/* Message Content */}
          <div className="space-y-4">
            {(messageType === "voice" || messageType === "video") && (
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  הקלטת {messageType === "voice" ? "קול" : "וידאו"}
                </label>

                <div className="bg-muted rounded-lg p-4 space-y-3">
                  {recordingState === "idle" && (
                    <div className="text-center space-y-2">
                      <div className="text-sm text-muted-foreground">
                        לחץ להתחלת הקלטה (עד 60 שניות)
                      </div>
                      <Button onClick={startRecording} className="gap-2">
                        {messageType === "voice" ? (
                          <Mic className="h-4 w-4" />
                        ) : (
                          <Video className="h-4 w-4" />
                        )}
                        התחל הקלטה
                      </Button>
                    </div>
                  )}

                  {recordingState === "recording" && (
                    <div className="space-y-2">
                      {/* Video preview during recording */}
                      {messageType === "video" && (
                        <video
                          ref={(el) => {
                            if (el && streamRef.current) {
                              el.srcObject = streamRef.current;
                              el.play().catch((err) => {
                                console.log("Play was prevented:", err);
                              });
                            }
                          }}
                          className="w-full rounded-md bg-black"
                          muted
                          playsInline
                        />
                      )}

                      <div className="text-center space-y-2">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span className="font-mono text-lg">
                            {formatTime(recordingTime)}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          מקליט...
                        </div>
                        <Button
                          onClick={stopRecording}
                          variant="destructive"
                          className="gap-2"
                        >
                          <Square className="h-4 w-4" />
                          עצור הקלטה
                        </Button>
                      </div>
                    </div>
                  )}

                  {recordingState === "recorded" && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="gap-1">
                          {messageType === "voice" ? (
                            <Mic className="h-3 w-3" />
                          ) : (
                            <Video className="h-3 w-3" />
                          )}
                          {formatTime(recordingTime)}
                        </Badge>
                        <div className="flex gap-2">
                          <Button
                            onClick={isPlaying ? pauseRecording : playRecording}
                            variant="outline"
                            size="sm"
                            className="gap-1"
                          >
                            {isPlaying ? (
                              <Pause className="h-3 w-3" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                            {isPlaying ? "השהה" : "נגן"}
                          </Button>
                          <Button
                            onClick={resetRecording}
                            variant="outline"
                            size="sm"
                            className="gap-1"
                          >
                            <RotateCcw className="h-3 w-3" />
                            הקלט שוב
                          </Button>
                        </div>
                      </div>

                      {/* Audio playback element */}
                      {messageType === "voice" && (
                        <audio ref={audioRef} className="hidden" />
                      )}

                      {/* Video playback element */}
                      {messageType === "video" && (
                        <video
                          ref={videoRef}
                          className="w-full rounded-md bg-black"
                          controls={false}
                        />
                      )}

                      <div className="text-xs text-muted-foreground text-center">
                        ההקלטה מוכנה לשליחה
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {messageType === "image" && (
              <div className="space-y-3">
                <label className="text-sm font-medium">בחר תמונה</label>

                <div className="bg-muted rounded-lg p-4 space-y-3">
                  {mediaBlob && imagePreview ? (
                    <div className="space-y-2">
                      <Image
                        src={imagePreview}
                        alt="תצוגה מקדימה של התמונה"
                        width={600}
                        height={400}
                        className="w-full rounded-md object-cover max-h-60"
                        unoptimized
                      />
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => imageInputRef.current?.click()}
                          className="gap-1"
                        >
                          <ImageIcon className="h-3 w-3" />
                          החלף תמונה
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSubmitError(null);
                            clearMedia();
                          }}
                          className="gap-1"
                        >
                          <X className="h-3 w-3" />
                          הסר
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="text-sm text-muted-foreground">
                        העלה תמונה אחת שתישלח למתנדב
                      </div>
                      <Button
                        onClick={() => imageInputRef.current?.click()}
                        className="gap-2"
                      >
                        <ImageIcon className="h-4 w-4" />
                        העלה תמונה
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {messageType === "text" ? "הודעה" : "טקסט נוסף (אופציונלי)"}
              </label>
              <Textarea
                placeholder="כתוב הודעת תודה אישית למתנדב..."
                value={textMessage}
                onChange={(e) => {
                  setTextMessage(e.target.value.slice(0, 500));
                  if (submitError) {
                    setSubmitError(null);
                  }
                }}
                className="min-h-[100px] resize-none"
              />
              <div className="text-xs text-muted-foreground text-left">
                {textMessage.length}/500 תווים
              </div>
            </div>
          </div>

          {/* Quick Templates */}
          <div className="space-y-2">
            <label className="text-sm font-medium">תבניות מהירות</label>
            <div className="flex flex-wrap gap-2">
              {[
                "תודה רבה על העזרה המהירה!",
                "מתנדב מדהים, ממליץ בחום",
                "שירות מעולה ויחס אישי",
                "הציל לי את היום!",
              ].map((template) => (
                <Button
                  key={template}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTextMessage(template);
                    setSubmitError(null);
                  }}
                  className="text-xs h-auto py-1 px-2"
                >
                  {template}
                </Button>
              ))}
            </div>
          </div>

          {submitError && (
            <div className="text-xs text-red-600 bg-red-100/80 border border-red-200 rounded p-2 text-center">
              {submitError}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                (messageType === "text" && !textMessage.trim()) ||
                ((messageType === "voice" ||
                  messageType === "video" ||
                  messageType === "image") &&
                  !mediaBlob)
              }
              className="flex-1 gap-2"
            >
              {isSubmitting ? (
                submitStage === "converting" ? (
                  "ממיר מדיה..."
                ) : (
                  "שולח..."
                )
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  שלח הודעה
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onClose}>
              ביטול
            </Button>
          </div>

          {/* Privacy Note */}
          <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
            ההודעה תישלח למתנדב ותעזור לנו לשפר את השירות. המידע מוגן ולא יועבר
            לצדדים שלישיים.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

async function normalizeMediaBlob(
  blob: Blob,
  kind: MessageType
): Promise<Blob> {
  switch (kind) {
    case "voice":
      return convertAudioToMp3(blob);
    case "video":
      return convertVideoToMp4(blob);
    case "image":
      return convertImageToJpeg(blob);
    default:
      return blob;
  }
}
